import {
  addDays as addDaysFns,
  addMonths,
  differenceInCalendarDays,
  endOfMonth,
  format,
  getISOWeek,
  isValid,
  parseISO,
  startOfWeek as startOfWeekFns,
} from 'date-fns'
import { nl } from 'date-fns/locale'

/**
 * DE datummodule van de app (webshop, Mijn omgeving én beheer).
 *
 * Regels:
 * - Datums worden bewaard en verstuurd als "YYYY-MM-DD" in LOKALE tijd (nooit toISOString(), dat is UTC).
 * - "Vandaag" is overal dezelfde kalenderdag; verschillen worden in kalenderdagen geteld.
 * - Opmaak altijd Nederlands via date-fns, zonder punt achter afkortingen ("3 okt", "za").
 */

// ——— Omzetten ———

/** Date → "2026-10-03" (lokale tijd). */
export const iso = (d: Date) => format(d, 'yyyy-MM-dd')
/** "2026-10-03" → Date op lokale middernacht. Ongeldige invoer (bijv. uit de URL) → vandaag, nooit een "Invalid Date". */
export const fromIso = (date: string) => {
  const d = parseISO(date)
  return isValid(d) ? d : new Date()
}
/** Is dit een geldige "YYYY-MM-DD"? */
export const isIsoDate = (date: unknown): date is string =>
  typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date) && isValid(parseISO(date))
/** Vandaag als "YYYY-MM-DD". */
export const today = () => iso(new Date())

// ——— Tijdstempels (*At-velden) ———

/** Een stempel met zone ("…Z" of "…+02:00") rekenen we om naar de salon; zonder zone is hij al lokaal. */
const ZONED = /(?:Z|[+-]\d{2}:?\d{2})$/i
const amsterdam = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Amsterdam',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})

function stampParts(at: string): { date: string; time: string } {
  const s = at.trim()
  if (ZONED.test(s)) {
    const d = new Date(s)
    if (!Number.isNaN(d.getTime())) {
      const p = Object.fromEntries(amsterdam.formatToParts(d).map((x) => [x.type, x.value]))
      return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` }
    }
  }
  const m = /^(\d{4}-\d{2}-\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(s)
  return { date: m?.[1] ?? '', time: m?.[2] ? `${m[2]}:${m[3]}` : '' }
}

/** "2026-10-18T14:05" of "2026-10-18T12:05:00Z" → "14:05" (salontijd). Zonder tijd: "". */
export const timeOf = (at: string) => stampParts(at).time
/** "2026-10-18T14:05" of "2026-10-18T22:30:00Z" → "2026-10-18" / "2026-10-19" (salondag). */
export const dateOf = (at: string) => stampParts(at).date

/** Nieuwste eerst op `date` ("YYYY-MM-DD"), als kopie. Eén regel voor bezoeken, bestellingen en dagboek. */
export const newestFirst = <T extends { date: string }>(list: readonly T[]): T[] => [...list].sort((a, b) => b.date.localeCompare(a.date))

/** Sorteersleutel voor een moment met datum en tijd ("HH:mm", altijd met voorloopnul). */
const startKey = (x: { date: string; time: string }) => `${x.date}T${x.time}`
/** Vroegste eerst; draai a en b om voor laatste eerst. */
export const byStart = (a: { date: string; time: string }, b: { date: string; time: string }) => startKey(a).localeCompare(startKey(b))
/** Nu als "YYYY-MM-DDTHH:mm" in lokale tijd, voor ontvangstmomenten. */
export const nowStamp = () => format(new Date(), "yyyy-MM-dd'T'HH:mm")

// ——— Salontijd ———

/** De salon staat in Amsterdam. Afspraken zijn wandkloktijd dáár, ongeacht waar de klant is. */
export const SALON_TIME_ZONE = 'Europe/Amsterdam'

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/**
 * De weekdag (0 = zondag … 6 = zaterdag) zoals het nu in de salon is, niet op het toestel.
 * Een klant in New York om 23:00 op maandag ziet zo al "dinsdag": het is daar dan al dinsdag in Amsterdam.
 */
export function salonWeekday(now: Date = new Date()): number {
  const short = new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: SALON_TIME_ZONE }).format(now)
  const i = WEEKDAY_SHORT.indexOf(short)
  return i === -1 ? now.getDay() : i
}

const zoneFormatters = new Map<string, Intl.DateTimeFormat>()
const zoneFormatter = (timeZone: string) => {
  let f = zoneFormatters.get(timeZone)
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
    zoneFormatters.set(timeZone, f)
  }
  return f
}

/** Verschil tussen de wandklok in `timeZone` en UTC op dat moment, in ms (zomertijd NL: +2 uur). */
const zoneOffsetMs = (instant: number, timeZone: string) => {
  const parts = zoneFormatter(timeZone).formatToParts(new Date(instant))
  const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value)
  const wallAsUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'))
  return wallAsUtc - Math.floor(instant / 1000) * 1000
}

/**
 * "2026-10-25" + "10:30" in salontijd → het exacte moment (Date).
 *
 * Waarom niet `parseISO('2026-10-25T10:30')`: dat leest de tijd in de tijdzone van het
 * apparaat. Een klant op vakantie in Lissabon zou dan een uur verkeerd zien of de
 * 24-uursregel een uur te vroeg of te laat raken. Hier is het altijd Amsterdam.
 *
 * Zomer/wintertijd: de offset wordt twee keer bepaald (rond het geschatte moment), zodat
 * ook een tijd vlak na de klokwissel klopt. Tijden die niet bestaan (02:30 bij de
 * lentewissel) schuiven een uur op; de salon is dan dicht.
 */
export function zonedInstant(date: string, time: string, timeZone = SALON_TIME_ZONE): Date {
  const [y, m, d] = date.split('-').map(Number)
  const [h, min] = time.split(':').map(Number)
  const wall = Date.UTC(y, m - 1, d, h, min)
  if (!Number.isFinite(wall)) return new Date(NaN)
  const first = wall - zoneOffsetMs(wall, timeZone)
  return new Date(wall - zoneOffsetMs(first, timeZone))
}

/** Hele uren tussen nu en een moment (afgerond naar beneden, zoals date-fns `differenceInHours`). */
export const hoursFromNow = (instant: Date, now: Date = new Date()) => Math.trunc((instant.getTime() - now.getTime()) / 3_600_000)

// ——— Rekenen ———

export const addDays = (d: Date, days: number) => addDaysFns(d, days)
/** "YYYY-MM-DD" + n dagen → "YYYY-MM-DD". */
export const shiftDate = (date: string, days: number) => iso(addDaysFns(parseISO(date), days))
export const daysUntil = (date: string) => differenceInCalendarDays(parseISO(date), new Date())
/** Kalenderdagen van `from` tot `to` ("YYYY-MM-DD"), ongevoelig voor de klokwissel. */
export const daysBetween = (from: string, to: string) => differenceInCalendarDays(parseISO(to), parseISO(from))
export const daysSince = (date: string) => -daysUntil(date)
export const weeksSince = (date: string) => Math.max(0, Math.floor(daysSince(date) / 7))

/** "YYYY-MM-DD" + n maanden; de dag wordt afgekapt op de laatste dag (31 jan + 1 = 28/29 feb). */
export const shiftMonths = (date: string, months: number) => iso(addMonths(parseISO(date), months))
/** Eerste dag van de maand van `date`: "2026-10-01". */
export const monthStart = (date: string) => `${date.slice(0, 7)}-01`
/** Laatste dag van de maand van `date`: "2026-10-31". */
export const monthEnd = (date: string) => iso(endOfMonth(parseISO(date)))
/** Eerste dag van het kwartaal van `date`: "2026-10-01". */
export const quarterStart = (date: string) => {
  const month = Math.floor((Number(date.slice(5, 7)) - 1) / 3) * 3 + 1
  return `${date.slice(0, 4)}-${String(month).padStart(2, '0')}-01`
}
/** "okt 2026" */
export const monthShort = (date: string) => stripDots(format(parseISO(date), 'MMM yyyy', { locale: nl }))

/** Maandag van de week waarin `d` valt (00:00). */
export const startOfWeek = (d: Date) => startOfWeekFns(d, { weekStartsOn: 1 })
/** ISO-weeknummer. */
export const weekNumber = (d: Date) => getISOWeek(d)
export const weekDays = (monday: Date) => Array.from({ length: 7 }, (_, i) => addDaysFns(monday, i))

// ——— Opmaak (ISO-tekst in) ———

/** "zaterdag 3 oktober" */
export const dayLong = (date: string) => format(parseISO(date), 'EEEE d MMMM', { locale: nl })
/** "3 okt" */
export const dayShort = (date: string) => stripDots(format(parseISO(date), 'd MMM', { locale: nl }))
/** "za 3 okt" */
export const dayMedium = (date: string) => stripDots(format(parseISO(date), 'EEEEEE d MMM', { locale: nl }))

// ——— Opmaak (Date in) ———

/** "3 okt" */
export const shortDate = (d: Date) => dayShort(iso(d))
/** "zaterdag 3 oktober" */
export const longDate = (d: Date) => dayLong(iso(d))
/** "3 oktober 2026" (o.a. de datum boven een artikel) */
export const fullDate = (d: Date) => format(d, 'd MMMM yyyy', { locale: nl })
/** "zaterdag" */
export const weekdayLong = (d: Date) => format(d, 'EEEE', { locale: nl })
/** "za" */
// 'EEEEEE' geeft in het Nederlands de korte vorm zonder punt ("za"); 'EEE' zou "zat" geven.
export const weekdayShort = (d: Date) => stripDots(format(d, 'EEEEEE', { locale: nl }))

// ——— Relatief ———

/** Klanttekst, midden in een zin: "vandaag", "morgen", "over 9 dagen", "3 dagen geleden". */
export const relative = (date: string) => {
  const d = daysUntil(date)
  if (d === 0) return 'vandaag'
  if (d === 1) return 'morgen'
  if (d === -1) return 'gisteren'
  return d > 0 ? `over ${d} dagen` : `${-d} dagen geleden`
}

/** Label in lijsten (beheer): "Vandaag", "Morgen", "Gisteren" of "za 3 okt". Zelfde dagtelling als `relative`. */
export const relativeDay = (date: string) => {
  if (!date) return 'Geen datum'
  const d = daysUntil(date)
  if (d === 0) return 'Vandaag'
  if (d === 1) return 'Morgen'
  if (d === -1) return 'Gisteren'
  return dayMedium(date)
}

const stripDots = (text: string) => text.replace(/\./g, '')
